import { API_BASE } from '@/shared/services/api';
/**
 * EzTask API Service
 * Handles all communication with the EzTask backend API
 */

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  const tenantId = localStorage.getItem('ezerp_tenant_id');
  const orgId = localStorage.getItem('ezerp_org_id');
  
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Phase 2: Context Transport Hints
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  if (orgId) headers['X-Org-Node-ID'] = orgId;

  return headers;
};

/**
 * Shared request helper — surfaces real server errors instead of generic messages.
 * Network failures throw "Backend server is offline — please start the backend."
 * HTTP errors throw the server's JSON detail or status text.
 */
async function apiRequest(url, options = {}) {
  let response;
  try {
    response = await fetch(url, options);
  } catch (networkErr) {
    const isOffline = networkErr.message?.toLowerCase().includes('failed to fetch') ||
                      networkErr.message?.toLowerCase().includes('connection refused') ||
                      networkErr.message?.toLowerCase().includes('network');
    throw new Error(
      isOffline
        ? 'Backend server is offline — please start the backend (Start_EzERP.bat).'
        : `Network error: ${networkErr.message}`
    );
  }
  if (!response.ok) {
    let detail = `Server error ${response.status}`;
    try {
      const body = await response.json();
      detail = body?.detail || body?.message || JSON.stringify(body);
    } catch {
      detail = response.statusText || detail;
    }
    throw new Error(detail);
  }
  return response.json();
}


// Board APIs
export const boardApi = {
  list: async (archived = false, starred = null) => {
    const params = new URLSearchParams({ archived: archived.toString() });
    if (starred !== null) params.append('starred', starred.toString());
    return apiRequest(`${API_URL}/api/eztask/boards?${params}`, {
      headers: getAuthHeaders()
    });
  },

  get: async (boardId) => {
    return apiRequest(`${API_URL}/api/eztask/boards/${boardId}`, {
      headers: getAuthHeaders()
    });
  },

  create: async (data) => {
    return apiRequest(`${API_URL}/api/eztask/boards`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },

  update: async (boardId, data) => {
    return apiRequest(`${API_URL}/api/eztask/boards/${boardId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },

  archive: async (boardId) => {
    return apiRequest(`${API_URL}/api/eztask/boards/${boardId}/archive`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
  },

  addMember: async (boardId, userId, role = 'member') => {
    const response = await fetch(`${API_URL}/api/eztask/boards/${boardId}/members`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ user_id: userId, role })
    });
    if (!response.ok) throw new Error('Failed to add member');
    return response.json();
  },

  removeMember: async (boardId, userId) => {
    const response = await fetch(`${API_URL}/api/eztask/boards/${boardId}/members/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to remove member');
    return response.json();
  },

  uploadBackground: async (boardId, file) => {
    const token = localStorage.getItem('ezerpToken');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/eztask/boards/${boardId}/background`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    if (!response.ok) throw new Error('Failed to upload board background');
    return response.json();
  }
};

// List APIs
export const listApi = {
  create: async (boardId, data) => {
    const response = await fetch(`${API_URL}/api/eztask/boards/${boardId}/lists`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create list');
    return response.json();
  },

  update: async (listId, data) => {
    const response = await fetch(`${API_URL}/api/eztask/lists/${listId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update list');
    return response.json();
  },

  archive: async (listId) => {
    const response = await fetch(`${API_URL}/api/eztask/lists/${listId}/archive`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to archive list');
    return response.json();
  },

  reorder: async (boardId, items) => {
    const response = await fetch(`${API_URL}/api/eztask/boards/${boardId}/lists/reorder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ items })
    });
    if (!response.ok) throw new Error('Failed to reorder lists');
    return response.json();
  }
};

// Card APIs
export const cardApi = {
  create: async (listId, data) => {
    const response = await fetch(`${API_URL}/api/eztask/lists/${listId}/cards`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create card');
    return response.json();
  },

  get: async (cardId) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch card');
    return response.json();
  },

  update: async (cardId, data) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update card');
    return response.json();
  },

  move: async (cardId, listId, position = null) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/move`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ list_id: listId, position })
    });
    if (!response.ok) throw new Error('Failed to move card');
    return response.json();
  },

  archive: async (cardId) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/archive`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to archive card');
    return response.json();
  },

  reorder: async (listId, items) => {
    const response = await fetch(`${API_URL}/api/eztask/lists/${listId}/cards/reorder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ items })
    });
    if (!response.ok) throw new Error('Failed to reorder cards');
    return response.json();
  },

  addMember: async (cardId, userId) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/members/${userId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to add member');
    return response.json();
  },

  removeMember: async (cardId, userId) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/members/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to remove member');
    return response.json();
  },

  addLabel: async (cardId, labelId) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/labels/${labelId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to add label');
    return response.json();
  },

  removeLabel: async (cardId, labelId) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/labels/${labelId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to remove label');
    return response.json();
  },

  // Phase 3: HITL Approval
  approve: async (cardId, comment = '') => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/approve`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(comment ? { comment } : {})
    });
    if (!response.ok) throw new Error('Failed to approve card');
    return response.json();
  }
};

// Checklist APIs
export const checklistApi = {
  create: async (cardId, data) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/checklists`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create checklist');
    return response.json();
  },

  addItem: async (checklistId, data) => {
    const response = await fetch(`${API_URL}/api/eztask/checklists/${checklistId}/items`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to add item');
    return response.json();
  },

  toggleItem: async (itemId, isComplete) => {
    const response = await fetch(`${API_URL}/api/eztask/checklists/items/${itemId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ is_complete: isComplete })
    });
    if (!response.ok) throw new Error('Failed to update item');
    return response.json();
  }
};

// Comment APIs
export const commentApi = {
  create: async (cardId, content) => {
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/comments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content })
    });
    if (!response.ok) throw new Error('Failed to create comment');
    return response.json();
  },

  update: async (commentId, content) => {
    const response = await fetch(`${API_URL}/api/eztask/comments/${commentId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content })
    });
    if (!response.ok) throw new Error('Failed to update comment');
    return response.json();
  },

  delete: async (commentId) => {
    const response = await fetch(`${API_URL}/api/eztask/comments/${commentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete comment');
    return response.json();
  }
};

// Label APIs
export const labelApi = {
  create: async (boardId, data) => {
    const response = await fetch(`${API_URL}/api/eztask/boards/${boardId}/labels`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create label');
    return response.json();
  }
};

// Dashboard APIs
export const dashboardApi = {
  get: async () => {
    const response = await fetch(`${API_URL}/api/eztask/dashboard`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch dashboard');
    return response.json();
  }
};

// Automation APIs
export const automationApi = {
  list: async (boardId) => {
    const response = await fetch(`${API_URL}/api/eztask/boards/${boardId}/automations`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch automations');
    return response.json();
  },

  create: async (boardId, data) => {
    const response = await fetch(`${API_URL}/api/eztask/boards/${boardId}/automations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create automation');
    return response.json();
  },

  test: async (automationId, cardId) => {
    const response = await fetch(`${API_URL}/api/eztask/automations/${automationId}/test?card_id=${cardId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to test automation');
    return response.json();
  }
};

// Attachment APIs
export const attachmentApi = {
  upload: async (cardId, file, isCover = false) => {
    const token = localStorage.getItem('ezerpToken');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/eztask/cards/${cardId}/attachments?is_cover=${isCover}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    if (!response.ok) throw new Error('Failed to upload attachment');
    return response.json();
  },

  delete: async (attachmentId) => {
    const response = await fetch(`${API_URL}/api/eztask/attachments/${attachmentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete attachment');
    return response.json();
  }
};
